import Switch from "@/v2-ui/switch"
import Label from "@/v2-ui/label"
import LegalBillingTenantGroupRow from "@/v2-backoffice-client/legalBilling/LegalBillingTenantGroupRow"
import LegalBillingTotalsSummary from "@/v2-backoffice-client/legalBilling/LegalBillingTotalsSummary"
import { hideInvoicedFromSummary } from "@/v2-backoffice-client/legalBilling/legalBilling.utils"
import type {
  LegalBillingInvoiceAction,
  LegalBillingSummary,
} from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingBoardProps = {
  summary: LegalBillingSummary
  isHidingInvoiced: boolean
  onHidingInvoicedChange(isHiding: boolean): void
  isPending?: boolean
  onInvoice(action: LegalBillingInvoiceAction): void
}

// The board: range-wide totals, the hide-invoiced toggle, and the tenant → tag → batch
// tree. Presentational on purpose — the page owns the query, the mutation and the date
// range, so this whole tree renders from plain props and is testable without Apollo.
function LegalBillingBoard(props: LegalBillingBoardProps) {
  const {
    summary,
    isHidingInvoiced,
    onHidingInvoicedChange,
    isPending,
    onInvoice,
  } = props

  // Hiding filters ROWS only; every figure still describes the whole range (see
  // hideInvoicedFromSummary). Totals that shrank with the filter would let an admin read a
  // smaller number, invoice it, and miss the rest.
  const visible = isHidingInvoiced ? hideInvoicedFromSummary(summary) : summary

  return (
    <div className="legal-billing-board">
      <div className="flex items-center justify-between gap-4 mb-4">
        <LegalBillingTotalsSummary
          totals={summary.Totals}
          invoiced={summary.Invoiced}
          isRoot
        />

        <div className="flex items-center gap-2">
          <Switch
            id="legal-billing-hide-invoiced"
            checked={isHidingInvoiced}
            data-feature-code="UI_BackofficeLegalBilling_Board_HideInvoicedToggle"
            aria-label="Hide already-invoiced rows"
            onCheckedChange={onHidingInvoicedChange}
          />
          <Label htmlFor="legal-billing-hide-invoiced" className="text-sm">
            Hide invoiced
          </Label>
        </div>
      </div>

      {/* Invoiced rows are visible by DEFAULT: an admin reconciling an invoice needs to
          see what was already billed, and a view that silently omits it turns "already
          invoiced" into "never purchased". */}
      {visible.Tenants.length === 0 ? (
        <p className="legal-billing-empty text-sm text-muted-foreground">
          {summary.Tenants.length === 0
            ? "No document purchases in this range."
            : "Every purchase in this range is invoiced."}
        </p>
      ) : (
        visible.Tenants.map((tenant) => (
          <LegalBillingTenantGroupRow
            key={tenant.TenantId}
            tenant={tenant}
            isPending={isPending}
            onInvoice={onInvoice}
          />
        ))
      )}
    </div>
  )
}

export default LegalBillingBoard
